import React, { useState, useEffect } from 'react';
import { useAuth, API } from '@/App';
import axios from 'axios';
import {
  TrendingUp,
  Package,
  Clock,
  Smile,
  Meh,
  Frown,
  BarChart3,
  ShoppingBag,
  DollarSign,
  MessageSquare,
  Loader2
} from 'lucide-react';

// Format currency
const formatCurrency = (amount) => {
  return new Intl.NumberFormat('id-ID', {
    style: 'currency',
    currency: 'IDR',
    minimumFractionDigits: 0
  }).format(amount || 0);
};

// Top Products Component
export const TopProductsWidget = ({ data, loading }) => {
  if (loading) {
    return (
      <div className="bg-white dark:bg-zinc-900 rounded-lg p-3 border border-slate-200 dark:border-zinc-800">
        <div className="flex items-center justify-center h-28">
          <Loader2 className="w-5 h-5 animate-spin text-indigo-600 dark:text-indigo-400" />
        </div>
      </div>
    );
  }

  return (
    <div className="bg-white dark:bg-zinc-900 rounded-lg p-3 border border-slate-200 dark:border-zinc-800">
      <div className="flex items-center gap-2 mb-3">
        <Package className="w-4 h-4 text-purple-600 dark:text-purple-400" />
        <h3 className="font-semibold text-slate-900 dark:text-zinc-50 text-xs">
          Produk Terlaris
        </h3>
      </div>
      
      {data && data.length > 0 ? (
        <div className="space-y-2">
          {data.slice(0, 3).map((product, index) => (
            <div key={index} className="flex items-center justify-between text-xs">
              <div className="flex items-center gap-2 min-w-0 flex-1">
                <span className={`
                  w-5 h-5 rounded-full flex items-center justify-center text-[10px] font-bold flex-shrink-0
                  ${index === 0 ? 'bg-amber-100 text-amber-700' : 
                    index === 1 ? 'bg-slate-200 text-slate-700' :
                    'bg-orange-100 text-orange-700'} dark:text-zinc-200 dark:bg-amber-950/40`}>
                  {index + 1}
                </span>
                <span className="text-slate-700 dark:text-zinc-300 truncate">
                  {product.product || 'Unknown'}
                </span>
              </div>
              <span className="font-semibold text-slate-900 dark:text-zinc-50 shrink-0 ml-2">
                {product.total_sold}
              </span>
            </div>
          ))}
        </div>
      ) : (
        <div className="text-center py-4">
          <ShoppingBag className="w-6 h-6 mx-auto text-slate-500 dark:text-zinc-500 mb-1" />
          <p className="text-xs text-slate-500 dark:text-zinc-400">Belum ada data</p>
        </div>
      )}
    </div>
  );
};

// Peak Hours Component
export const PeakHoursWidget = ({ data, peakSummary, loading }) => {
  if (loading) {
    return (
      <div className="bg-white dark:bg-zinc-900 rounded-lg p-3 border border-slate-200 dark:border-zinc-800">
        <div className="flex items-center justify-center h-28">
          <Loader2 className="w-5 h-5 animate-spin text-indigo-600 dark:text-indigo-400" />
        </div>
      </div>
    );
  }

  const maxChats = data ? Math.max(...data.map(h => h.chat_count)) : 0;

  return (
    <div className="bg-white dark:bg-zinc-900 rounded-lg p-3 border border-slate-200 dark:border-zinc-800">
      <div className="flex items-center justify-between mb-3">
        <div className="flex items-center gap-2">
          <Clock className="w-4 h-4 text-indigo-600 dark:text-indigo-400" />
          <h3 className="font-semibold text-slate-900 dark:text-zinc-50 text-xs">
            Jam Ramai
          </h3>
        </div>
        {peakSummary && peakSummary.hour !== null && (
          <span className="text-[10px] bg-indigo-100 dark:bg-blue-900/50 text-indigo-700 dark:text-indigo-300 px-1.5 py-0.5 rounded">
            Peak {peakSummary.label}
          </span>
        )}
      </div>
      
      {data && data.length > 0 ? (
        <div className="flex items-end gap-0.5 h-16">
          {/* Show hours: 6-22 */}
          {data.filter(h => h.hour >= 6 && h.hour <= 22).map((hourData) => {
            const percentage = maxChats > 0 ? (hourData.chat_count / maxChats) * 100 : 0;
            const isPeak = peakSummary && hourData.hour === peakSummary.hour;
            
            return (
              <div 
                key={hourData.hour} 
                className="flex-1 flex flex-col items-center justify-end h-full"
                title={`${hourData.label}: ${hourData.chat_count} chat`}
              >
                <div 
                  className={`w-full rounded-t transition-all ${
                    isPeak ? 'bg-indigo-500' :
                    percentage >= 50 ? 'bg-blue-400' :
                    percentage >= 20 ? 'bg-blue-300' :
                    'bg-slate-200 dark:bg-zinc-700'
                  }`}
                  style={{ height: `${maxChats > 0 ? Math.max(percentage, 10) : 10}%` }}
                />
              </div>
            );
          })}
        </div>
      ) : (
        <div className="text-center py-4">
          <MessageSquare className="w-6 h-6 mx-auto text-slate-500 dark:text-zinc-500 mb-1" />
          <p className="text-xs text-slate-500 dark:text-zinc-400">Belum ada data</p>
        </div>
      )}
      
      {data && data.length > 0 && (
        <div className="flex justify-between mt-1 text-[9px] text-slate-500 dark:text-zinc-400">
          <span>6:00</span>
          <span>14:00</span>
          <span>22:00</span>
        </div>
      )}
    </div>
  );
};

// Customer Satisfaction Component
export const SatisfactionWidget = ({ data, loading }) => {
  if (loading) {
    return (
      <div className="bg-white dark:bg-zinc-900 rounded-lg p-3 border border-slate-200 dark:border-zinc-800">
        <div className="flex items-center justify-center h-28">
          <Loader2 className="w-5 h-5 animate-spin text-indigo-600 dark:text-indigo-400" />
        </div>
      </div>
    );
  }

  const total = data?.total || 0;
  const satisfactionRate = data?.satisfaction_rate || 0;
  
  const getColor = () => {
    if (satisfactionRate >= 70) return 'text-indigo-600 dark:text-indigo-400';
    if (satisfactionRate >= 40) return 'text-amber-600';
    return 'text-red-600';
  };

  const getBgColor = () => {
    if (satisfactionRate >= 70) return 'bg-indigo-500';
    if (satisfactionRate >= 40) return 'bg-amber-500';
    return 'bg-red-500';
  };

  return (
    <div className="bg-white dark:bg-zinc-900 rounded-lg p-3 border border-slate-200 dark:border-zinc-800">
      <div className="flex items-center gap-2 mb-3">
        <TrendingUp className="w-4 h-4 text-indigo-600 dark:text-indigo-400" />
        <h3 className="font-semibold text-slate-900 dark:text-zinc-50 text-xs">
          Kepuasan
        </h3>
      </div>
      
      {total > 0 ? (
        <div>
          <div className="flex items-center gap-2 mb-2">
            <span className={`text-2xl font-bold ${getColor()}`}>
              {satisfactionRate}%
            </span>
            <span className="text-xs text-slate-500 dark:text-zinc-400">puas</span>
          </div>
          
          {/* Progress bar */}
          <div className="h-2 bg-slate-100 dark:bg-zinc-800 rounded-full overflow-hidden mb-2">
            <div 
              className={`h-full rounded-full transition-all ${getBgColor()}`}
              style={{ width: `${satisfactionRate}%` }}
            />
          </div>
          
          <div className="flex justify-between text-[10px] text-slate-500 dark:text-zinc-400">
            <span className="flex items-center gap-0.5">
              <Smile className="w-3 h-3 text-blue-500" />
              {data?.positive || 0}
            </span>
            <span className="flex items-center gap-0.5">
              <Meh className="w-3 h-3 text-slate-500 dark:text-zinc-400" />
              {data?.neutral || 0}
            </span>
            <span className="flex items-center gap-0.5">
              <Frown className="w-3 h-3 text-red-500" />
              {data?.negative || 0}
            </span>
          </div>
        </div>
      ) : (
        <div className="text-center py-4">
          <Smile className="w-6 h-6 mx-auto text-slate-500 dark:text-zinc-500 mb-1" />
          <p className="text-xs text-slate-500 dark:text-zinc-400">Belum ada data</p>
        </div>
      )}
    </div>
  );
};

// Main Dashboard Analytics Component
export const DashboardAnalytics = ({ period = 'week', channel = 'all' }) => {
  const { token } = useAuth();
  const [loading, setLoading] = useState(true);
  const [analytics, setAnalytics] = useState(null);
  
  const headers = { Authorization: `Bearer ${token}` };

  useEffect(() => {
    fetchAnalytics();
  }, [period, channel]);

  const fetchAnalytics = async () => {
    try {
      setLoading(true);
      const response = await axios.get(`${API}/dashboard/analytics?period=${period}&channel=${channel}`, { headers });
      if (response.data.success) {
        setAnalytics(response.data);
      }
    } catch (error) {
      console.error('Error fetching dashboard analytics:', error);
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
      <TopProductsWidget 
        data={analytics?.top_products} 
        loading={loading} 
      />
      <PeakHoursWidget 
        data={analytics?.peak_hours}
        peakSummary={analytics?.peak_hour_summary}
        loading={loading} 
      />
      <SatisfactionWidget 
        data={analytics?.satisfaction} 
        loading={loading} 
      />
    </div>
  );
};

export default DashboardAnalytics;
